/**
 * /apply/privacy — 체험단 서비스 개인정보처리방침 (참여자용 공개 페이지)
 *
 * 원문은 노션(inwv.notion.site/1bpluschool-seeding-privacy-policy)에서 그대로 옮겼다(REQ-199).
 * 문구를 바꾸면 시행일을 갱신하고, 제10조대로 시행 7일 전부터 공지한다.
 * 신청 폼의 동의 문구(ApplicationForm)와 수집 항목·제공 항목이 어긋나지 않게 함께 본다.
 */
import { createFileRoute } from '@tanstack/react-router'
import {
  Article,
  Bullets,
  LegalShell,
  LegalTable,
  Numbered,
  SubHeading,
} from '@/features/apply/components/LegalDocument'

export const Route = createFileRoute('/apply/privacy')({
  component: SeedingPrivacyPage,
})

function SeedingPrivacyPage() {
  return (
    <LegalShell
      title="개인정보처리방침"
      effectiveAt="2026년 3월 1일"
      intro={
        <p>
          주식회사 인사이트웨이브(이하 "회사")는 「개인정보 보호법」에 따라 이용자의 개인정보를 보호하고, 이와 관련한
          고충을 신속하고 원활하게 처리할 수 있도록 다음과 같이 개인정보처리방침을 수립·공개합니다.
        </p>
      }
    >
      <Article title="제1조 (개인정보의 수집 항목 및 수집 방법)">
        <SubHeading>1. 수집 항목</SubHeading>
        <p>회사는 체험단 서비스 제공을 위해 다음의 개인정보를 수집합니다.</p>
        <p className="font-medium text-on-surface">필수 항목</p>
        <Bullets items={['이름, 휴대전화번호']} />
        <p className="font-medium text-on-surface">선택 항목 (캠페인에 따라 수집 여부가 다를 수 있습니다)</p>
        <Bullets
          items={[
            '이메일, 성별, 생년월일',
            '배송지 정보 (우편번호, 기본주소, 상세주소)',
            'SNS 계정 정보 (계정 ID, 팔로워 수)',
            '카페24 쇼핑몰 회원 ID',
            '미션 제출물 (사진, 영상, URL)',
          ]}
        />
        <p className="font-medium text-on-surface">자동 수집 항목</p>
        <Bullets items={['접속 IP, 브라우저 종류, 접속 일시']} />
        <SubHeading>2. 수집 방법</SubHeading>
        <Bullets
          items={[
            '체험단 신청 페이지를 통한 직접 입력',
            '미션 제출 페이지를 통한 콘텐츠 업로드',
            '서비스 이용 과정에서 자동 생성·수집',
          ]}
        />
      </Article>

      <Article title="제2조 (개인정보의 수집·이용 목적)">
        <p>회사는 수집한 개인정보를 다음의 목적을 위해 이용합니다.</p>
        <LegalTable
          head={['목적', '상세']}
          rows={[
            ['체험단 운영', '신청 접수, 참여자 선정, 선정 결과 안내'],
            ['상품 배송', '체험 상품 배송지 확인 및 배송'],
            ['미션 관리', '미션 제출 확인, 콘텐츠 검수 및 승인'],
            ['리워드 지급', '쿠폰 발급, 적립금 지급 등'],
            ['본인 확인', 'SMS 인증을 통한 신청자 본인 확인'],
            ['서비스 개선', '서비스 이용 통계 분석 및 품질 개선'],
          ]}
        />
      </Article>

      <Article title="제3조 (개인정보의 제3자 제공)">
        <p>회사는 이용자의 동의를 받아 다음과 같이 개인정보를 제3자에게 제공합니다.</p>
        <LegalTable
          head={['항목', '내용']}
          rows={[
            ['제공받는 자', '캠페인을 운영하는 쇼핑몰 운영사 (캠페인별 상이)'],
            [
              '제공 항목',
              '이름, 전화번호, 이메일, 성별, 생년월일, 배송지 정보, SNS 계정 정보 (캠페인에서 수집한 항목에 한함)',
            ],
            ['제공 목적', '체험 상품 배송, 미션 작성 확인, 리워드 지급'],
            ['보유 기간', '제공 목적 달성 후 6개월'],
          ]}
        />
        <p>※ 이용자는 제3자 제공에 대한 동의를 거부할 수 있으나, 거부 시 체험단 신청이 제한됩니다.</p>
      </Article>

      <Article title="제4조 (개인정보 처리 위탁)">
        <p>회사는 서비스 제공을 위해 다음과 같이 개인정보 처리를 위탁하고 있습니다.</p>
        <LegalTable
          head={['수탁업체', '위탁 업무']}
          rows={[
            ['Google Cloud Platform (GCP)', '클라우드 서버 운영 및 데이터 보관'],
            ['카카오', '알림톡(SMS) 발송'],
          ]}
        />
      </Article>

      <Article title="제5조 (개인정보의 보유 및 이용 기간)">
        <p>
          회사는 개인정보 수집·이용 목적이 달성된 후에는 해당 정보를 지체 없이 파기합니다. 다만, 다음의 경우에는 아래
          기간 동안 보유합니다.
        </p>
        <LegalTable
          head={['보유 근거', '보유 기간']}
          rows={[
            ['체험단 캠페인 운영 목적', '캠페인 종료 후 6개월'],
            ['전자상거래 등에서의 소비자 보호에 관한 법률 — 계약 또는 청약철회에 관한 기록', '5년'],
            ['전자상거래 등에서의 소비자 보호에 관한 법률 — 소비자 불만 또는 분쟁처리에 관한 기록', '3년'],
            ['통신비밀보호법 — 접속 로그 기록', '3개월'],
          ]}
        />
      </Article>

      <Article title="제6조 (개인정보의 파기 절차 및 방법)">
        <Numbered
          items={[
            '파기 절차: 보유 기간이 경과하거나 처리 목적이 달성된 개인정보는 별도의 DB로 옮겨(종이의 경우 별도 서류함) 내부 방침 및 관련 법령에 따라 일정 기간 저장된 후 파기됩니다.',
            '파기 방법: 전자적 파일 형태의 정보는 복구할 수 없는 기술적 방법을 사용하여 삭제하고, 종이에 출력된 개인정보는 분쇄기로 분쇄하거나 소각합니다.',
          ]}
        />
      </Article>

      <Article title="제7조 (이용자의 권리·의무 및 행사 방법)">
        <p>이용자는 언제든지 다음의 권리를 행사할 수 있습니다.</p>
        <Numbered items={['개인정보 열람 요구', '오류 등이 있을 경우 정정 요구', '삭제 요구', '처리 정지 요구']} />
        <p>
          권리 행사는 아래 개인정보 보호책임자에게 이메일로 요청하실 수 있으며, 회사는 이에 대해 지체 없이
          조치하겠습니다.
        </p>
      </Article>

      <Article title="제8조 (개인정보의 안전성 확보 조치)">
        <p>회사는 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다.</p>
        <Bullets
          items={[
            '개인정보의 암호화 (비밀번호, 전화번호 등 민감 정보 암호화 저장)',
            '접속 기록의 보관 및 위·변조 방지',
            '개인정보에 대한 접근 제한 (접근 권한 관리)',
            'SSL/TLS를 통한 네트워크 구간 암호화',
          ]}
        />
      </Article>

      <Article title="제9조 (개인정보 보호책임자)">
        <p>
          회사는 개인정보 처리에 관한 업무를 총괄하고, 이용자의 불만 처리 및 피해 구제를 위하여 아래와 같이 개인정보
          보호책임자를 지정하고 있습니다.
        </p>
        <Bullets
          items={[
            '개인정보 보호책임자: 최수임',
            <>
              이메일:{' '}
              <a href="mailto:1bpluschool@inwv.co" className="underline hover:text-on-surface">
                1bpluschool@inwv.co
              </a>
            </>,
            '소속: 주식회사 인사이트웨이브',
          ]}
        />
      </Article>

      <Article title="제10조 (개인정보처리방침의 변경)">
        <p>
          이 개인정보처리방침은 시행일로부터 적용되며, 변경 사항이 있을 경우 시행일 7일 전부터 웹사이트를 통해
          공지합니다.
        </p>
        <Bullets items={['시행일: 2026년 3월 1일']} />
      </Article>
    </LegalShell>
  )
}
